import React, {
    useMemo,
    useState
} from "react"
import {
    Box,
    Paper,
    Typography,
    TextField,
    InputAdornment,
    Button,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Skeleton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Tooltip,
    useTheme,
    useMediaQuery
} from "@mui/material"
import {
    AddOutlined,
    SearchOutlined,
    EditOutlined,
    DeleteOutlineOutlined,
    CloseOutlined,
    RefreshOutlined,
    ReceiptLongOutlined,
    WarningAmberOutlined
} from "@mui/icons-material"
import { Message, toaster } from "rsuite"
import _ from "lodash"
import {
    useGetAllFeeQuery,
    useCreatePlatformFeeMutation,
    useEditPlatformFeeMutation,
    useDeletePlatformFeeMutation
} from "../Redux/actions/setting.action"

const currency = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2
})

const formatAmount = (amount) => {
    const value = Number(amount)
    return Number.isFinite(value) ? currency.format(value) : amount
}

const errorMessage = (error, fallback) => {
    const detail = _.get(error, "data.detail")
    return _.isString(detail) ? detail : fallback
}

const notify = (type, message) =>
    toaster.push(
        <Message showIcon type={type}>{message}</Message>,
        { placement: "topCenter" }
    )

const primaryButtonSx = {
    textTransform: "none",
    fontWeight: 700,
    borderRadius: "10px",
    px: 2.5,
    bgcolor: "var(--color-primary)",
    boxShadow: "none",
    "&:hover": { bgcolor: "var(--color-primary-hover)", boxShadow: "none" }
}

/* =========================
   ADD / EDIT DIALOG
========================= */

function FeeDialog({ fee, fees, onClose }) {
    const isEdit = Boolean(fee)
    const [name, setName] = useState(fee ? fee.fee_name : "")
    const [amount, setAmount] = useState(fee ? fee.fee_amount : "")
    const [touched, setTouched] = useState(false)

    const [createFee, createState] = useCreatePlatformFeeMutation()
    const [editFee, editState] = useEditPlatformFeeMutation()
    const isSaving = createState.isLoading || editState.isLoading

    const trimmedName = name.trim()
    const duplicate = fees.some(
        (item) =>
            item.id !== fee?.id &&
            item.fee_name.trim().toLowerCase() === trimmedName.toLowerCase()
    )

    const nameError = !trimmedName
        ? "Fee name is required"
        : duplicate
            ? "A fee with this name already exists"
            : ""
    const amountError = amount === ""
        ? "Amount is required"
        : !Number.isFinite(Number(amount))
            ? "Enter a valid amount"
            : Number(amount) < 0
                ? "Amount cannot be negative"
                : ""

    const unchanged = isEdit &&
        trimmedName === fee.fee_name &&
        Number(amount) === Number(fee.fee_amount)

    const handleSave = async () => {
        setTouched(true)
        if (nameError || amountError || unchanged) return

        try {
            const payload = { fee_name: trimmedName, fee_amount: String(amount).trim() }
            const result = isEdit
                ? await editFee({ fee_ref: fee.id, ...payload }).unwrap()
                : await createFee(payload).unwrap()
            notify("success", _.get(result, "message", isEdit ? "Fee updated" : "Fee added"))
            onClose()
        } catch (error) {
            notify("error", errorMessage(error, `Could not ${isEdit ? "update" : "add"} the fee. Please try again.`))
        }
    }

    return (
        <Dialog
            open
            onClose={isSaving ? undefined : onClose}
            fullWidth
            maxWidth="xs"
            PaperProps={{ sx: { borderRadius: "18px" } }}
        >
            <DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, pb: 1 }}>
                <Box>
                    <Typography sx={{ fontFamily: "var(--font-heading)", fontWeight: 700, fontSize: 20 }}>
                        {isEdit ? "Edit fee" : "Add fee"}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "var(--text-secondary)" }}>
                        {isEdit ? fee.fee_name : "Create a new platform fee"}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} disabled={isSaving} aria-label="Close">
                    <CloseOutlined fontSize="small" />
                </IconButton>
            </DialogTitle>

            <Box
                component="form"
                noValidate
                onSubmit={(e) => {
                    e.preventDefault()
                    handleSave()
                }}
            >
                <DialogContent sx={{ pt: "8px !important", display: "flex", flexDirection: "column", gap: 2.5 }}>
                    <TextField
                        label="Fee name"
                        placeholder="e.g. Annual subscription"
                        fullWidth
                        autoFocus
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        error={touched && Boolean(nameError)}
                        helperText={touched && nameError}
                        inputProps={{ maxLength: 300 }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                    />
                    <TextField
                        label="Amount"
                        placeholder="0"
                        type="number"
                        fullWidth
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        error={touched && Boolean(amountError)}
                        helperText={
                            touched && amountError
                                ? amountError
                                : amount !== "" && !amountError
                                    ? formatAmount(amount)
                                    : " "
                        }
                        inputProps={{ min: 0, step: "0.01" }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>
                        }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                    />
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button
                        onClick={onClose}
                        disabled={isSaving}
                        sx={{ textTransform: "none", color: "var(--text-secondary)", fontWeight: 600 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isSaving || unchanged}
                        sx={primaryButtonSx}
                    >
                        {isSaving ? "Saving…" : isEdit ? "Save changes" : "Add fee"}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    )
}

/* =========================
   DELETE DIALOG
========================= */

function DeleteFeeDialog({ fee, onClose }) {
    const [deleteFee, deleteState] = useDeletePlatformFeeMutation()

    const handleDelete = async () => {
        try {
            const result = await deleteFee({ fee_ref: fee.id }).unwrap()
            notify("success", _.get(result, "message", "Fee deleted"))
            onClose()
        } catch (error) {
            notify("error", errorMessage(error, "Could not delete the fee. Please try again."))
        }
    }

    return (
        <Dialog
            open
            onClose={deleteState.isLoading ? undefined : onClose}
            fullWidth
            maxWidth="xs"
            PaperProps={{ sx: { borderRadius: "18px" } }}
        >
            <DialogTitle sx={{ fontFamily: "var(--font-heading)", fontWeight: 700, fontSize: 20, pb: 1 }}>
                Delete fee?
            </DialogTitle>
            <DialogContent>
                <Box sx={{
                    display: "flex",
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: "12px",
                    bgcolor: "var(--color-danger-light)",
                    color: "#991b1b"
                }}>
                    <WarningAmberOutlined fontSize="small" sx={{ mt: "2px" }} />
                    <Typography sx={{ fontSize: 13, lineHeight: 1.5 }}>
                        <strong>{fee.fee_name}</strong> ({formatAmount(fee.fee_amount)}) will be
                        removed. This cannot be undone.
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button
                    onClick={onClose}
                    disabled={deleteState.isLoading}
                    sx={{ textTransform: "none", color: "var(--text-secondary)", fontWeight: 600 }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    onClick={handleDelete}
                    disabled={deleteState.isLoading}
                    startIcon={<DeleteOutlineOutlined />}
                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", px: 2.5, boxShadow: "none" }}
                >
                    {deleteState.isLoading ? "Deleting…" : "Delete"}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

/* =========================
   PAGE
========================= */

export default function PlatformFee() {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("md"))

    const { data, isLoading, isFetching, isError, error, refetch } = useGetAllFeeQuery()

    const [search, setSearch] = useState("")
    // undefined = closed, null = adding, fee object = editing
    const [editingFee, setEditingFee] = useState(undefined)
    const [deletingFee, setDeletingFee] = useState(null)

    const fees = useMemo(() => _.get(data, "list", []) || [], [data])

    const summary = useMemo(() => {
        const amounts = fees.map((fee) => Number(fee.fee_amount)).filter(Number.isFinite)
        const highest = _.maxBy(fees, (fee) => Number(fee.fee_amount) || 0)
        return {
            count: fees.length,
            total: _.sum(amounts),
            highest
        }
    }, [fees])

    const visibleFees = useMemo(() => {
        const query = search.trim().toLowerCase()
        return fees
            .filter((fee) => !query || fee.fee_name.toLowerCase().includes(query))
            .sort((a, b) => a.fee_name.localeCompare(b.fee_name))
    }, [fees, search])

    const openAdd = () => setEditingFee(null)

    if (isError) {
        return (
            <Paper elevation={0} sx={{ p: 5, textAlign: "center", borderRadius: "16px", border: "1px solid var(--border-color)" }}>
                <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>
                    Couldn't load platform fees
                </Typography>
                <Typography sx={{ color: "var(--text-secondary)", fontSize: 14, mb: 3 }}>
                    {errorMessage(error, "Check that the server is running, then try again.")}
                </Typography>
                <Button variant="outlined" startIcon={<RefreshOutlined />} onClick={refetch} sx={{ textTransform: "none" }}>
                    Try again
                </Button>
            </Paper>
        )
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

            {/* ================= SUMMARY ================= */}

            <Box sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)" },
                gap: 2
            }}>
                {[
                    { label: "Fees configured", value: summary.count },
                    { label: "Total of all fees", value: formatAmount(summary.total) },
                    {
                        label: "Highest fee",
                        value: summary.highest ? formatAmount(summary.highest.fee_amount) : "—",
                        caption: summary.highest?.fee_name
                    }
                ].map((card, index) => (
                    <Paper
                        key={card.label}
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: "14px",
                            border: "1px solid var(--border-color)",
                            gridColumn: { xs: index === 0 ? "1 / -1" : "auto", md: "auto" },
                            minWidth: 0
                        }}
                    >
                        <Typography sx={{ fontSize: 12, color: "var(--text-secondary)", fontWeight: 500 }}>
                            {card.label}
                        </Typography>
                        {isLoading ? (
                            <Skeleton width={80} height={36} />
                        ) : (
                            <Typography
                                noWrap
                                sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 700, color: "var(--text-primary)", fontFamily: "var(--font-heading)" }}
                            >
                                {card.value}
                            </Typography>
                        )}
                        {card.caption && (
                            <Typography noWrap sx={{ fontSize: 12, color: "var(--text-light)" }}>
                                {card.caption}
                            </Typography>
                        )}
                    </Paper>
                ))}
            </Box>

            {/* ================= LIST ================= */}

            <Paper elevation={0} sx={{ borderRadius: "16px", border: "1px solid var(--border-color)", overflow: "hidden" }}>

                <Box sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "stretch", sm: "center" },
                    gap: 1.5,
                    p: 2,
                    borderBottom: "1px solid var(--border-color)"
                }}>
                    <TextField
                        size="small"
                        placeholder="Search fees"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "#f8fafc" } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlined fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                        <Tooltip title="Refresh">
                            <span>
                                <IconButton
                                    onClick={refetch}
                                    disabled={isFetching}
                                    sx={{ border: "1px solid var(--border-color)", borderRadius: "10px" }}
                                    aria-label="Refresh fees"
                                >
                                    <RefreshOutlined fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Button
                            variant="contained"
                            startIcon={<AddOutlined />}
                            onClick={openAdd}
                            sx={{ ...primaryButtonSx, flex: { xs: 1, sm: "none" }, whiteSpace: "nowrap" }}
                        >
                            Add fee
                        </Button>
                    </Box>
                </Box>

                {isLoading ? (
                    <Box sx={{ p: 2 }}>
                        {[0, 1, 2].map((row) => (
                            <Skeleton key={row} height={56} sx={{ borderRadius: "10px" }} />
                        ))}
                    </Box>
                ) : visibleFees.length === 0 ? (
                    <Box sx={{ py: 7, px: 3, textAlign: "center" }}>
                        <ReceiptLongOutlined sx={{ fontSize: 40, color: "var(--text-light)", mb: 1 }} />
                        <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                            {fees.length === 0 ? "No platform fees yet" : "No fees match your search"}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: "var(--text-secondary)", mb: 2.5 }}>
                            {fees.length === 0
                                ? "Add the fees your platform charges to get started."
                                : "Try a different name."}
                        </Typography>
                        {fees.length === 0 ? (
                            <Button variant="contained" startIcon={<AddOutlined />} onClick={openAdd} sx={primaryButtonSx}>
                                Add your first fee
                            </Button>
                        ) : (
                            <Button onClick={() => setSearch("")} sx={{ textTransform: "none", fontWeight: 600 }}>
                                Clear search
                            </Button>
                        )}
                    </Box>
                ) : isMobile ? (

                    /* Mobile list */
                    <Box>
                        {visibleFees.map((fee) => (
                            <Box
                                key={fee.id}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    p: 2,
                                    borderBottom: "1px solid #f1f5f9",
                                    "&:last-child": { borderBottom: "none" }
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography noWrap sx={{ fontSize: 14, fontWeight: 600 }}>
                                        {fee.fee_name}
                                    </Typography>
                                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: "var(--color-primary)" }}>
                                        {formatAmount(fee.fee_amount)}
                                    </Typography>
                                </Box>
                                <IconButton onClick={() => setEditingFee(fee)} aria-label={`Edit ${fee.fee_name}`}>
                                    <EditOutlined fontSize="small" />
                                </IconButton>
                                <IconButton
                                    onClick={() => setDeletingFee(fee)}
                                    aria-label={`Delete ${fee.fee_name}`}
                                    sx={{ color: "var(--color-danger)" }}
                                >
                                    <DeleteOutlineOutlined fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                ) : (

                    /* Desktop table */
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{
                                    "& th": {
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: "var(--text-light)",
                                        textTransform: "uppercase",
                                        letterSpacing: ".04em",
                                        bgcolor: "#f8fafc",
                                        borderBottom: "1px solid var(--border-color)"
                                    }
                                }}>
                                    <TableCell sx={{ width: 64 }}>#</TableCell>
                                    <TableCell>Fee name</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                    <TableCell align="right" sx={{ width: 140 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleFees.map((fee, index) => (
                                    <TableRow
                                        key={fee.id}
                                        hover
                                        sx={{ "& td": { borderBottom: "1px solid #f1f5f9" }, "&:last-child td": { borderBottom: "none" } }}
                                    >
                                        <TableCell sx={{ color: "var(--text-light)", fontSize: 13 }}>
                                            {index + 1}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>
                                            {fee.fee_name}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                                            {formatAmount(fee.fee_amount)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                            <Tooltip title="Edit fee">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setEditingFee(fee)}
                                                    aria-label={`Edit ${fee.fee_name}`}
                                                    sx={{
                                                        border: "1px solid var(--border-color)",
                                                        borderRadius: "8px",
                                                        color: "var(--text-secondary)",
                                                        "&:hover": { color: "var(--color-primary)", borderColor: "var(--color-primary)", bgcolor: "var(--color-primary-light)" }
                                                    }}
                                                >
                                                    <EditOutlined fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete fee">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setDeletingFee(fee)}
                                                    aria-label={`Delete ${fee.fee_name}`}
                                                    sx={{
                                                        ml: 1,
                                                        border: "1px solid var(--border-color)",
                                                        borderRadius: "8px",
                                                        color: "var(--text-light)",
                                                        "&:hover": { color: "var(--color-danger)", borderColor: "var(--color-danger)", bgcolor: "var(--color-danger-light)" }
                                                    }}
                                                >
                                                    <DeleteOutlineOutlined fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {editingFee !== undefined && (
                <FeeDialog
                    key={editingFee ? editingFee.id : "new"}
                    fee={editingFee}
                    fees={fees}
                    onClose={() => setEditingFee(undefined)}
                />
            )}

            {deletingFee && (
                <DeleteFeeDialog
                    key={deletingFee.id}
                    fee={deletingFee}
                    onClose={() => setDeletingFee(null)}
                />
            )}
        </Box>
    )
}
